import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type {
  ApprovalRequest,
  CodexRun,
  ConversationMessage,
  RunActivityTree,
  RunCommandActivityNode,
  RunCommandOutputLine,
  RunEvent
} from '../types';
import { groupCommandsByHighLevelStage } from './activityTree';

export type RunnerStepStatus = 'running' | 'succeeded' | 'failed';

export type RunnerCommandEvent = 'started' | 'succeeded' | 'failed';

export type RunnerStep = {
  id: string;
  phase: string;
  step: string;
  status: RunnerStepStatus;
  command?: string;
  exitCode?: number | null;
  outputs: { id: number; stream: string; text: string }[];
  startedAt?: string | null;
  finishedAt?: string | null;
};

export type RunnerState = {
  message: string;
  phase: string | null;
  progress: number | null;
  steps: RunnerStep[];
  activeStepId: string | null;
};

export type RunnerCommandUpdate = {
  kind: 'command';
  phase: string | null;
  step: string;
  event: RunnerCommandEvent;
  status: RunnerStepStatus;
  command?: string;
  exitCode?: number;
  commandId?: string;
};

export type RunnerStatusUpdate = {
  kind: 'status';
  phase: string | null;
  message: string;
};

export type RunnerErrorUpdate = {
  kind: 'error';
  message: string;
};

export type RunnerMetricUpdate = {
  kind: 'metric';
  message: string;
};

export type RunnerProgressUpdate = {
  kind: 'progress';
  phase: string | null;
  message: string;
};

export type RunnerUpdate =
  | RunnerCommandUpdate
  | RunnerStatusUpdate
  | RunnerErrorUpdate
  | RunnerMetricUpdate
  | RunnerProgressUpdate;

type ActivitySummaryStatus = 'running' | 'succeeded' | 'failed';
type ActivitySummaryActionKind = 'command' | 'output' | 'error';

type ActivitySummaryEntry = {
  id: string;
  eventId: number;
  stageId: string;
  stageName: string;
  step: string;
  status: ActivitySummaryStatus;
  text: string;
  createdAt: string;
  actionKind?: ActivitySummaryActionKind;
  actionKey?: string;
  commandText?: string;
};

type MarkdownTextProps = {
  text: string;
  className: string;
};

export const MarkdownText = ({ text, className }: MarkdownTextProps) => (
  <div className={className}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

const RUNNER_PHASE_HEADLINES: Record<
  string,
  { running: string; succeeded: string; failed: string; status: string }
> = {
  init: {
    running: 'Preparing your request',
    succeeded: 'Request prepared',
    failed: 'Failed while preparing request',
    status: 'Preparing your request'
  },
  build: {
    running: 'Preparing the agent environment',
    succeeded: 'Agent environment is ready',
    failed: 'Failed to prepare the agent environment',
    status: 'Preparing the agent environment'
  },
  validate: {
    running: 'Checking the environment',
    succeeded: 'Environment checks passed',
    failed: 'Environment checks failed',
    status: 'Checking the environment'
  },
  workspace: {
    running: 'Preparing the workspace',
    succeeded: 'Workspace is ready',
    failed: 'Workspace setup failed',
    status: 'Preparing the workspace'
  },
  setup: {
    running: 'Configuring the session',
    succeeded: 'Session configured',
    failed: 'Session setup failed',
    status: 'Configuring the session'
  },
  run: {
    running: 'Agent is running',
    succeeded: 'Agent finished running',
    failed: 'Agent run failed',
    status: 'Agent is running'
  },
  cleanup: {
    running: 'Finalizing the run',
    succeeded: 'Run finalized',
    failed: 'Finalization failed',
    status: 'Finalizing the run'
  },
  complete: {
    running: 'Finishing response',
    succeeded: 'Response is ready',
    failed: 'Failed while finishing response',
    status: 'Finishing response'
  }
};

const RUNNER_PHASE_COMPLETION_MESSAGES: Partial<Record<string, RegExp>> = {
  workspace: /^workspace ready(?:\s*\([^)]*\))?$/i
};

const ACTIVITY_STAGE_LABELS: Record<string, string> = {
  timeline: 'Timeline',
  thinking: 'Thinking',
  actions: 'Command execution',
  updates: 'Progress updates',
  compaction: 'Compaction',
  init: 'Preparing request',
  build: 'Preparing environment',
  validate: 'Validation',
  workspace: 'Workspace setup',
  setup: 'Session setup',
  run: 'Agent run',
  cleanup: 'Finalizing',
  complete: 'Completed'
};

export const humanizeRunnerStatus = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === 'running') {
    return 'In progress';
  }
  if (normalized === 'succeeded') {
    return 'Done';
  }
  if (normalized === 'failed') {
    return 'Failed';
  }
  return status;
};

export const formatActivityDuration = (
  startedAt?: string | null,
  finishedAt?: string | null,
  nowMs = Date.now()
): string | null => {
  if (!startedAt) {
    return null;
  }
  const startedMs = new Date(startedAt).getTime();
  const finishedMs = finishedAt ? new Date(finishedAt).getTime() : nowMs;
  if (!Number.isFinite(startedMs) || !Number.isFinite(finishedMs)) {
    return null;
  }
  const elapsedMs = finishedMs - startedMs;
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) {
    return null;
  }
  if (elapsedMs < 1000) {
    return `${Math.round(elapsedMs)} ms`;
  }
  const seconds = elapsedMs / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`;
  }
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainder = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${remainder}s`;
};

export const stageLabelForId = (stageId: string, fallbackName: string) =>
  ACTIVITY_STAGE_LABELS[stageId] ?? ACTIVITY_STAGE_LABELS[fallbackName.toLowerCase()] ?? fallbackName;

export const isAgentContainerStep = (step: string) => step.toLowerCase().includes('agent container');

const THINKING_STEP_LABEL = 'thinking';
const ACTIONS_STAGE_LABEL = 'actions';
const RUNNER_TITLE_PREVIEW_MAX_LENGTH = 96;

const normalizeInlinePreview = (value: string): string => value.replace(/\s+/g, ' ').trim();

const truncateInlinePreview = (value: string, maxLength = RUNNER_TITLE_PREVIEW_MAX_LENGTH): string => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
};

const extractThinkingNodePreview = (command: RunCommandActivityNode): string | null => {
  const stage = command.stage.trim().toLowerCase();
  const step = command.step.trim().toLowerCase();
  const isThinkingNode = stage === THINKING_STEP_LABEL || step === THINKING_STEP_LABEL;
  if (!isThinkingNode) {
    return null;
  }
  let latestLine: string | null = null;
  for (let index = command.output_tail.length - 1; index >= 0; index -= 1) {
    const line = normalizeInlinePreview(command.output_tail[index]?.text ?? '');
    if (!line) {
      continue;
    }
    latestLine = line;
    break;
  }
  if (!latestLine) {
    return null;
  }
  const preview = latestLine.replace(/^thinking\s*[—:-]\s*/i, '');
  if (!preview || preview.toLowerCase() === THINKING_STEP_LABEL) {
    return null;
  }
  return truncateInlinePreview(preview);
};

const escapeRegex = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const extractActionNodePreview = (command: RunCommandActivityNode): string | null => {
  const stage = command.stage.trim().toLowerCase();
  if (stage !== ACTIONS_STAGE_LABEL) {
    return null;
  }
  let latestLine: string | null = null;
  for (let index = command.output_tail.length - 1; index >= 0; index -= 1) {
    const line = normalizeInlinePreview(command.output_tail[index]?.text ?? '');
    if (!line) {
      continue;
    }
    latestLine = line;
    break;
  }
  if (!latestLine) {
    return null;
  }
  const step = command.step.trim();
  const stepPattern = step ? new RegExp(`^${escapeRegex(step)}\\s*[—:-]\\s*`, 'i') : null;
  const withoutStepPrefix = stepPattern ? latestLine.replace(stepPattern, '') : latestLine;
  const preview = withoutStepPrefix
    .replace(/^(started|completed|failed)\s+/i, '')
    .replace(/^tool output\s*[—:-]\s*/i, '')
    .replace(/^bash output\s*[—:-]\s*/i, '')
    .trim();
  if (!preview || preview.toLowerCase() === step.toLowerCase()) {
    return null;
  }
  return truncateInlinePreview(preview);
};

export const extractActivityNodePreview = (command: RunCommandActivityNode): string | null =>
  extractThinkingNodePreview(command) ?? extractActionNodePreview(command);

const URL_IN_TEXT_PATTERN = /(https?:\/\/[^\s]+)/g;
const URL_TRAILING_PUNCTUATION = ',.;!?:';
const LEGACY_UNASSIGNED_COMMAND_ID = 'unassigned';
const FALLBACK_OUTPUT_COMMAND_ID = 'runner.output';
const FALLBACK_OUTPUT_STEP = 'Runner output';
const RUNNER_COMMAND_STEP_LABELS: Record<string, string> = {
  'runner.init': 'Init output',
  'runner.build': 'Build output',
  'runner.validate': 'Validation output',
  'runner.workspace': 'Workspace output',
  'runner.setup': 'Setup output',
  'runner.run': 'Run output',
  'runner.cleanup': 'Cleanup output',
  'runner.complete': 'Complete output',
  'runner.bootstrap': 'Bootstrap diagnostics',
  [FALLBACK_OUTPUT_COMMAND_ID]: FALLBACK_OUTPUT_STEP
};

const splitUrlAndTrailingPunctuation = (rawUrl: string): { url: string; trailing: string } => {
  let url = rawUrl;
  let trailing = '';
  while (url.length > 0) {
    const lastChar = url[url.length - 1];
    if (lastChar === ')') {
      const openCount = (url.match(/\(/g) ?? []).length;
      const closeCount = (url.match(/\)/g) ?? []).length;
      if (closeCount > openCount) {
        trailing = `${lastChar}${trailing}`;
        url = url.slice(0, -1);
        continue;
      }
      break;
    }
    if (URL_TRAILING_PUNCTUATION.includes(lastChar)) {
      trailing = `${lastChar}${trailing}`;
      url = url.slice(0, -1);
      continue;
    }
    break;
  }
  return { url, trailing };
};

export const renderTextWithLinks = (text: string): React.ReactNode => {
  const matches = Array.from(text.matchAll(URL_IN_TEXT_PATTERN));
  if (matches.length === 0) {
    return text;
  }
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < matches.length; i += 1) {
    const match = matches[i];
    const rawUrl = match[0];
    const { url, trailing } = splitUrlAndTrailingPunctuation(rawUrl);
    const start = match.index ?? 0;
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    if (url.length > 0) {
      nodes.push(
        <a
          key={`${url}:${start}:${i}`}
          className="runner-step__link"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          {url}
        </a>
      );
    } else {
      nodes.push(rawUrl);
    }
    if (trailing.length > 0) {
      nodes.push(trailing);
    }
    cursor = start + rawUrl.length;
  }
  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }
  return nodes;
};

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const extractTextFromUnknown = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (Array.isArray(value)) {
    const parts = value.map(extractTextFromUnknown).filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join('\n') : null;
  }
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  for (const key of ['text', 'output_text', 'message', 'delta', 'content', 'raw_content', 'output']) {
    const text = extractTextFromUnknown(record[key]);
    if (text) {
      return text;
    }
  }
  return null;
};

const parseJsonObject = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const normalizeJsonEventType = (value: string): string => value.replace(/[./_]/g, '').toLowerCase();

const formatReasoningJsonUpdate = (item: Record<string, unknown>, eventType: string): string => {
  const summary = extractTextFromUnknown(item.summary);
  const hidden = typeof item.encrypted_content === 'string' && item.encrypted_content.trim().length > 0;
  const emptySummary = Array.isArray(item.summary) && item.summary.length === 0;
  const emptyContent = item.content == null || (Array.isArray(item.content) && item.content.length === 0);
  if (summary) {
    return eventType.includes('failed') ? `Thinking failed — ${summary}` : `Thinking — ${summary}`;
  }
  if (eventType.includes('failed')) {
    return 'Thinking failed — reasoning hidden or empty';
  }
  return hidden || emptySummary || emptyContent ? 'Thinking — reasoning hidden or empty' : 'Thinking';
};

const formatCodexJsonUpdate = (raw: string): string | null => {
  const value = parseJsonObject(raw);
  if (!value) {
    return null;
  }
  const eventType = String(value.type ?? value.method ?? '');
  const normalizedEventType = normalizeJsonEventType(eventType);
  const payload =
    value.payload && typeof value.payload === 'object' && !Array.isArray(value.payload)
      ? (value.payload as Record<string, unknown>)
      : null;
  const params =
    value.params && typeof value.params === 'object' && !Array.isArray(value.params)
      ? (value.params as Record<string, unknown>)
      : null;
  const payloadType = String(payload?.type ?? '');
  if (normalizedEventType.includes('compact') || normalizeJsonEventType(payloadType).includes('compact')) {
    const detail = asNonEmptyString(value.message) ?? asNonEmptyString(payload?.message);
    return detail ? `Compaction — ${detail}` : 'Compaction — Context compacted';
  }
  if (
    payload &&
    ['agent_message', 'message', 'agentMessage'].includes(payloadType) &&
    (payloadType !== 'message' || payload.role === 'assistant')
  ) {
    const text =
      asNonEmptyString(payload.message) ?? extractTextFromUnknown(payload.content) ?? asNonEmptyString(payload.text);
    return text ? `Message — ${text}` : null;
  }
  if (normalizedEventType.includes('agentmessage')) {
    const text =
      asNonEmptyString(value.message) ??
      asNonEmptyString(value.delta) ??
      (params ? extractTextFromUnknown(params) : null);
    return text ? `Message — ${text}` : null;
  }
  const item =
    value.item && typeof value.item === 'object' && !Array.isArray(value.item)
      ? (value.item as Record<string, unknown>)
      : payload
        ? payload
        : params?.item && typeof params.item === 'object' && !Array.isArray(params.item)
          ? (params.item as Record<string, unknown>)
          : null;
  if (!item) {
    return null;
  }
  const itemType = String(item.type ?? '');
  if (itemType === 'reasoning') {
    return formatReasoningJsonUpdate(item, eventType);
  }
  if (itemType === 'agent_message' || itemType === 'message') {
    const text = asNonEmptyString(item.message) ?? extractTextFromUnknown(item.content) ?? asNonEmptyString(item.text);
    return text ? `Message — ${text}` : null;
  }
  const name =
    asNonEmptyString(item.name) ??
    asNonEmptyString(item.tool_name) ??
    asNonEmptyString(item.tool) ??
    asNonEmptyString(item.command);
  const suffix = name ? `${itemType} (${name})` : itemType || 'item';
  const status =
    eventType === 'response_item' && itemType === 'function_call'
      ? 'Started'
      : eventType.includes('failed')
        ? 'Failed'
        : eventType.includes('started')
          ? 'Started'
          : 'Completed';
  const args = item.arguments ?? item.input ?? item.args ?? item.parameters;
  const output = item.output ?? item.result ?? item.aggregated_output ?? item.stdout ?? item.stderr;
  let detail: string | null = null;
  if (args) {
    const parsedArgs = typeof args === 'string' ? parseJsonObject(args) : args;
    const argsRecord =
      parsedArgs && typeof parsedArgs === 'object' && !Array.isArray(parsedArgs)
        ? (parsedArgs as Record<string, unknown>)
        : null;
    const command = asNonEmptyString(argsRecord?.command) ?? asNonEmptyString(argsRecord?.cmd);
    detail = command ? `Bash: ${command}` : extractTextFromUnknown(args) ?? JSON.stringify(args);
  } else if (output || itemType.toLowerCase().includes('output')) {
    detail = `Tool output: ${extractTextFromUnknown(output ?? item) ?? ''}`.trim();
  }
  return detail ? `${status} ${suffix} — ${detail}` : `${status} ${suffix}`;
};

const normalizePromptOption = (value: unknown): { label: string; value: string } | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    return { label: trimmed, value: trimmed };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const option = value as Record<string, unknown>;
  const rawValue =
    asNonEmptyString(option.value) ??
    asNonEmptyString(option.id) ??
    asNonEmptyString(option.key) ??
    asNonEmptyString(option.name) ??
    asNonEmptyString(option.option);
  const rawLabel =
    asNonEmptyString(option.label) ??
    asNonEmptyString(option.title) ??
    asNonEmptyString(option.text) ??
    asNonEmptyString(option.message) ??
    rawValue;
  if (!rawValue || !rawLabel) {
    return null;
  }
  return { label: rawLabel, value: rawValue };
};

const extractPromptOptions = (value: Record<string, unknown>): Array<{ label: string; value: string }> => {
  const candidates = [
    value.options,
    value.choices,
    value.allowed_responses,
    value.responses,
    value.suggestions
  ];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }
    const options = candidate
      .map(normalizePromptOption)
      .filter((option): option is { label: string; value: string } => option !== null);
    if (options.length > 0) {
      return options;
    }
  }
  return [];
};

export const parseApprovalRequestFromPayload = (payload: string): ApprovalRequest | null => {
  const trimmed = payload.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const data = parsed as Record<string, unknown>;
  const type = String(data.type ?? '').toLowerCase();
  const options = extractPromptOptions(data);
  const allowFreeText = Boolean(
    data.allow_free_text === true ||
      data.allowTextInput === true ||
      data.allow_text_input === true ||
      data.free_text === true ||
      data.accepts_text === true
  );
  const requiresResponse = Boolean(
    data.requires_response === true ||
      data.awaiting_input === true ||
      data.needs_response === true ||
      data.waiting_for_input === true
  );
  const looksLikeApproval = type.includes('approval');
  const looksLikePrompt = type.includes('input') || type.includes('prompt') || type.includes('question');
  if (!looksLikeApproval && !looksLikePrompt && !requiresResponse && options.length === 0 && !allowFreeText) {
    return null;
  }

  const question =
    asNonEmptyString(data.question) ??
    asNonEmptyString(data.prompt) ??
    asNonEmptyString(data.message) ??
    asNonEmptyString(data.title);
  const detail =
    asNonEmptyString(data.command) ??
    asNonEmptyString(data.detail) ??
    asNonEmptyString(data.reason) ??
    asNonEmptyString(data.action) ??
    (!looksLikeApproval ? question : null);

  const kind: 'approval' | 'input' = looksLikeApproval ? 'approval' : 'input';
  const fallbackTitle = kind === 'approval' ? 'Approval requested' : 'Input requested';

  return {
    id:
      asNonEmptyString(data.id) ??
      asNonEmptyString(data.request_id) ??
      asNonEmptyString(data.approval_id),
    title: question ?? fallbackTitle,
    detail,
    raw: trimmed,
    kind,
    options: options.length > 0 ? options : undefined,
    allow_free_text: allowFreeText,
    placeholder:
      asNonEmptyString(data.placeholder) ??
      asNonEmptyString(data.input_placeholder) ??
      asNonEmptyString(data.response_placeholder),
    submit_label:
      asNonEmptyString(data.submit_label) ??
      asNonEmptyString(data.action_label) ??
      asNonEmptyString(data.cta)
  };
};

export const totalActivityCommandCount = (tree: RunActivityTree | null) =>
  tree ? tree.stages.reduce((sum, stage) => sum + stage.commands.length, 0) : 0;

export const mergeActivityTree = (existing: RunActivityTree, incoming: RunActivityTree): RunActivityTree => {
  const stageMap = new Map(
    existing.stages.map((stage) => [
      stage.id,
      {
        ...stage,
        commands: [...stage.commands]
      }
    ])
  );
  for (const incomingStage of incoming.stages) {
    const existingStage = stageMap.get(incomingStage.id);
    if (!existingStage) {
      stageMap.set(incomingStage.id, {
        ...incomingStage,
        commands: [...incomingStage.commands]
      });
      continue;
    }
    const commandMap = new Map(existingStage.commands.map((command) => [command.id, command]));
    for (const command of incomingStage.commands) {
      commandMap.set(command.id, command);
    }
    existingStage.commands = Array.from(commandMap.values()).sort(
      (left, right) => right.latest_event_id - left.latest_event_id
    );
    existingStage.latest_event_id = Math.max(existingStage.latest_event_id, incomingStage.latest_event_id);
    stageMap.set(incomingStage.id, existingStage);
  }
  const stages = Array.from(stageMap.values()).sort(
    (left, right) => right.latest_event_id - left.latest_event_id
  );
  return {
    ...existing,
    status: incoming.status,
    started_at: existing.started_at ?? incoming.started_at,
    finished_at: incoming.finished_at ?? existing.finished_at,
    has_more_commands: incoming.has_more_commands,
    next_before_event_id: incoming.next_before_event_id,
    stages
  };
};

export const compareConversationMessages = (
  left: Pick<ConversationMessage, 'id' | 'created_at'>,
  right: Pick<ConversationMessage, 'id' | 'created_at'>
) => {
  const createdAtDiff = new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
  if (createdAtDiff !== 0) {
    return createdAtDiff;
  }
  return left.id.localeCompare(right.id);
};

export const summarizeRunnerUpdate = (update: RunnerUpdate): string | null => {
  if (update.kind === 'error') {
    return 'Run failed';
  }
  if (update.kind === 'metric') {
    return null;
  }
  const phaseKey = (update.phase ?? '').trim().toLowerCase();
  const phaseCopy = RUNNER_PHASE_HEADLINES[phaseKey];
  if (update.kind === 'command') {
    if (phaseCopy) {
      return update.status === 'failed' ? phaseCopy.failed : phaseCopy.running;
    }
    if (update.status === 'failed') {
      return 'A run step failed';
    }
    if (update.status === 'succeeded') {
      return 'A run step completed';
    }
    return 'Working on your request';
  }
  if (phaseCopy) {
    const completionMessage = RUNNER_PHASE_COMPLETION_MESSAGES[phaseKey];
    if (completionMessage?.test(update.message.trim())) {
      return phaseCopy.succeeded;
    }
    return phaseCopy.status;
  }
  if (phaseKey === 'run') {
    return 'Agent is running';
  }
  return update.message.trim() || null;
};

export const parseAgentSummaryUpdate = (
  payload: string,
  eventId: number,
  createdAt: string
): ActivitySummaryEntry | null => {
  const trimmed = payload.trim();
  if (!trimmed || trimmed.startsWith('Runner ') || trimmed.startsWith('\u26A0')) {
    return null;
  }

  const parts = trimmed
    .split(' — ')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const headline = parts[0] ?? trimmed;
  const detail = parts.length > 1 ? parts.slice(1).join(' — ') : trimmed;
  const lower = trimmed.toLowerCase();
  const lowerHeadline = headline.toLowerCase();

  let stageId = 'updates';
  let stageName = 'Progress updates';
  if (lower.includes('compaction') || lower.includes('compacted')) {
    stageId = 'compaction';
    stageName = 'Compaction';
  } else if (lower.includes('thinking') || lower.includes('reasoning')) {
    stageId = 'thinking';
    stageName = 'Thinking';
  } else if (
    lower.includes('tool') ||
    lower.includes('bash:') ||
    lower.includes('bash output') ||
    lower.includes('tool output') ||
    lower.includes('tool error') ||
    lower.includes('command') ||
    lower.includes('read:') ||
    lower.includes('write:') ||
    lower.includes('edit:') ||
    lower.includes('search:')
  ) {
    stageId = 'actions';
    stageName = 'Command execution';
  }

  let status: ActivitySummaryStatus = 'running';
  if (lowerHeadline.startsWith('failed') || lower.includes(' error')) {
    status = 'failed';
  } else if (
    lowerHeadline.startsWith('completed') ||
    lowerHeadline.startsWith('done') ||
    lowerHeadline.startsWith('finished')
  ) {
    status = 'succeeded';
  }

  let step = headline;
  if (stageId === 'thinking') {
    step = 'Thinking';
  } else if (stageId === 'actions') {
    if (headline.includes(':')) {
      step = headline.split(':')[0]?.trim() || 'Command execution';
    } else if (lowerHeadline.startsWith('started ')) {
      step = headline.replace(/^started\s+/i, '').trim() || 'Command execution';
    } else if (lowerHeadline.startsWith('completed ')) {
      step = headline.replace(/^completed\s+/i, '').trim() || 'Command execution';
    } else if (lowerHeadline.startsWith('failed ')) {
      step = headline.replace(/^failed\s+/i, '').trim() || 'Command execution';
    }
  }
  if (!step) {
    step = stageName;
  }

  let text = detail.trim() || trimmed;
  let actionKind: ActivitySummaryActionKind | undefined;
  let actionKey: string | undefined;
  let commandText: string | undefined;

  if (stageId === ACTIONS_STAGE_LABEL) {
    const toolErrorMatch = trimmed.match(/^Tool error(?:\s*\(([^)]+)\))?\s*:?\s*(.*)$/i);
    if (toolErrorMatch) {
      const action = (toolErrorMatch[1] ?? 'Tool').trim() || 'Tool';
      step = action;
      status = 'failed';
      actionKind = 'error';
      actionKey = action.toLowerCase();
      text = (toolErrorMatch[2] ?? '').trim() || text;
    } else {
      const namedOutputMatch = trimmed.match(/^(.+?)\s+output\s*:\s*(.*)$/i);
      if (namedOutputMatch) {
        const action = namedOutputMatch[1]?.trim() || 'Tool';
        step = action;
        status = 'succeeded';
        actionKind = 'output';
        actionKey = action.toLowerCase();
        text = (namedOutputMatch[2] ?? '').trim() || text;
      } else {
        const commandMatch = trimmed.match(/^([^:]{1,80}):\s*(.*)$/);
        const detailCommandMatch = detail.match(/^([^:]{1,80}):\s*(.*)$/);
        if (
          status === 'running' &&
          detailCommandMatch &&
          detailCommandMatch[1]?.trim().toLowerCase() === 'bash'
        ) {
          actionKey = step.toLowerCase();
          actionKind = 'command';
          commandText = (detailCommandMatch[2] ?? '').trim();
          text = commandText || text;
        } else if (commandMatch) {
          const action = commandMatch[1]?.trim() || step;
          const command = (commandMatch[2] ?? '').trim();
          step = action;
          actionKey = action.toLowerCase();
          if (command) {
            commandText = command;
            text = command;
          }
          actionKind = status === 'running' ? 'command' : status === 'failed' ? 'error' : 'output';
        } else if (step) {
          actionKey = step.toLowerCase();
          actionKind = status === 'running' ? 'command' : status === 'failed' ? 'error' : 'output';
        }
      }
    }
  }

  return {
    id: `summary-${eventId}`,
    eventId,
    stageId,
    stageName,
    step,
    status,
    text,
    createdAt,
    actionKind,
    actionKey,
    commandText
  };
};

export const parseRunnerCommandUpdate = (payload: string): RunnerUpdate | null => {
  if (!payload.startsWith('Runner ')) {
    return null;
  }
  if (payload.startsWith('Runner error')) {
    return { kind: 'error', message: payload };
  }
  if (payload.startsWith('Runner metric')) {
    return { kind: 'metric', message: payload };
  }
  if (payload.startsWith('Runner progress')) {
    const fields = new Map<string, string>();
    for (const part of payload.split(' — ').slice(1)) {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex <= 0) {
        continue;
      }
      const key = part.slice(0, separatorIndex).trim().toLowerCase();
      const value = part.slice(separatorIndex + 1).trim();
      if (key) {
        fields.set(key, value);
      }
    }
    const phase = fields.get('phase') || null;
    const phaseCopy = RUNNER_PHASE_HEADLINES[(phase ?? '').trim().toLowerCase()];
    return {
      kind: 'progress',
      phase,
      message: phaseCopy?.status ?? 'Agent is processing'
    };
  }
  const parts = payload.split(' — ');
  if (parts.length < 2) {
    return { kind: 'status', phase: null as string | null, message: payload };
  }
  const phaseLabel = parts[0].replace('Runner ', '').trim();
  const phase = phaseLabel || null;
  if (parts.length < 3) {
    return { kind: 'status', phase, message: parts.slice(1).join(' — ').trim() };
  }
  const statusRaw = parts[2]?.trim().toLowerCase();
  if (!['started', 'succeeded', 'failed'].includes(statusRaw)) {
    return { kind: 'status', phase, message: parts.slice(1).join(' — ').trim() };
  }
  const event = statusRaw as RunnerCommandEvent;
  const status: RunnerStepStatus = event === 'started' ? 'running' : event;
  const step = parts[1]?.trim() || 'command';
  const tail = parts.slice(3);
  let commandId: string | undefined;
  let exitCode: number | undefined;
  if (tail.length > 1 && /^[a-zA-Z0-9_.:-]+$/.test(tail[tail.length - 1])) {
    commandId = tail.pop();
  }
  if (tail.length > 0 && tail[tail.length - 1].startsWith('exit ')) {
    const code = Number(tail.pop()?.replace('exit ', '').trim());
    if (!Number.isNaN(code)) {
      exitCode = code;
    }
  }
  const command = tail.join(' — ').trim();
  return {
    kind: 'command',
    phase,
    step,
    event,
    status,
    command: command || undefined,
    exitCode,
    commandId
  };
};

export const parseRunnerOutputLine = (payload: string) => {
  const commandMatch = payload.match(/^\[([a-zA-Z0-9_.:-]+)\]\s*(.*)$/);
  const commandId = commandMatch?.[1] ?? null;
  const remainder = commandMatch?.[2] ?? payload;
  const stepMatch = remainder.match(/^([^:]{2,80}):\s+(.*)$/);
  const step = stepMatch?.[1]?.trim() ?? null;
  const text = stepMatch?.[2] ?? remainder;
  return { commandId, step, text };
};

const parseUpdatePayloadObject = (value: string): Record<string, unknown> | null => {
  const trimmed = value.trim();
  if (!trimmed || !trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const normalizeUpdatePayload = (value: string) => {
  const parsed = parseUpdatePayloadObject(value);
  if (!parsed) {
    return value;
  }
  if (typeof parsed.message === 'string' && parsed.message.trim()) {
    return parsed.message;
  }
  const keys = Object.keys(parsed);
  if (keys.length === 1 && keys[0].toLowerCase() === 'ok' && parsed[keys[0]] == null) {
    return 'OK';
  }
  return value;
};

const parseServiceLogUpdate = (value: string) => {
  const parsed = parseUpdatePayloadObject(value);
  if (!parsed || parsed.type !== 'services.logs.available' || typeof parsed.service_log_id !== 'string') {
    return null;
  }
  const service =
    typeof parsed.service === 'object' && parsed.service
      ? (parsed.service as Record<string, unknown>)
      : null;
  const serviceName = typeof service?.name === 'string' ? service.name : null;
  const containerName = typeof service?.container === 'string' ? service.container : null;
  if (!serviceName || !containerName) {
    return null;
  }
  return {
    id: parsed.service_log_id,
    service_name: serviceName,
    container_name: containerName,
    bytes: typeof parsed.bytes === 'number' ? parsed.bytes : 0,
    truncated: parsed.truncated === true,
    message:
      typeof parsed.message === 'string' && parsed.message.trim()
        ? parsed.message
        : `Captured logs for ${serviceName}`
  };
};

export const buildRunActivityTreeFromEvents = (
  run: CodexRun | null,
  events: RunEvent[],
  outputTailLimit = 20
): RunActivityTree | null => {
  if (!run) {
    return null;
  }

  type MutableCommand = RunCommandActivityNode & {
    output_tail: RunCommandOutputLine[];
  };

  const commands = new Map<string, MutableCommand>();
  let activeCommandId: string | null = null;
  const activeActionCommandByKey = new Map<string, string>();
  let lastActiveActionCommandId: string | null = null;
  let latestEventId = 0;

  const getOrCreateCommand = (id: string, stage: string, step: string): MutableCommand => {
    const existing = commands.get(id);
    if (existing) {
      return existing;
    }
    const next: MutableCommand = {
      id,
      stage: stage || 'run',
      step: step || id,
      status: 'running',
      command: null,
      working_directory: run.cwd ?? null,
      exit_code: null,
      started_at: null,
      finished_at: null,
      output_count: 0,
      has_more_output: false,
      latest_event_id: 0,
      service_log: null,
      output_tail: []
    };
    commands.set(id, next);
    return next;
  };

  for (const event of events) {
    if (event.id > latestEventId) {
      latestEventId = event.id;
    }
    if (event.stream === 'update' || event.stream === 'raw') {
      const serviceLog = parseServiceLogUpdate(event.payload);
      if (serviceLog) {
        const command = getOrCreateCommand(
          `service-log-${serviceLog.id}`,
          'services',
          `${serviceLog.service_name} logs`
        );
        command.stage = 'services';
        command.step = `${serviceLog.service_name} logs`;
        command.status = 'succeeded';
        command.command = `docker logs --timestamps ${serviceLog.container_name}`;
        command.latest_event_id = Math.max(command.latest_event_id, event.id);
        command.started_at = command.started_at ?? event.created_at;
        command.finished_at = event.created_at;
        command.output_count = 1;
        command.service_log = {
          id: serviceLog.id,
          service_name: serviceLog.service_name,
          container_name: serviceLog.container_name,
          bytes: serviceLog.bytes,
          truncated: serviceLog.truncated
        };
        command.output_tail = [
          {
            id: event.id,
            stream: 'update',
            text: serviceLog.message,
            created_at: event.created_at
          }
        ];
        continue;
      }
      const updatePayload = (
        event.stream === 'raw' ? formatCodexJsonUpdate(event.payload) : normalizeUpdatePayload(event.payload)
      )?.trim();
      if (!updatePayload) {
        continue;
      }
      const parsed = parseRunnerCommandUpdate(updatePayload);
      if (parsed?.kind === 'command') {
        const phase = parsed.phase ?? 'run';
        const commandId = parsed.commandId ?? `event-${event.id}`;
        const step = parsed.step || commandId;
        const command = getOrCreateCommand(commandId, phase, step);
        command.stage = phase;
        command.step = step;
        command.status = parsed.status;
        command.command = parsed.command ?? command.command;
        command.working_directory = command.working_directory ?? run.cwd ?? null;
        command.exit_code = parsed.exitCode ?? command.exit_code;
        command.latest_event_id = Math.max(command.latest_event_id, event.id);
        if (parsed.event === 'started') {
          command.started_at = event.created_at;
          activeCommandId = commandId;
        } else {
          command.finished_at = event.created_at;
          if (activeCommandId === commandId) {
            activeCommandId = null;
          }
        }
        continue;
      }
      if (!parsed) {
        const summary = parseAgentSummaryUpdate(updatePayload, event.id, event.created_at);
        if (!summary) {
          continue;
        }

        let resolvedCommandId = summary.id;
        const isActionSummary = summary.stageId === ACTIONS_STAGE_LABEL;
        const isActionOutput = summary.actionKind === 'output' || summary.actionKind === 'error';
        const actionKey = summary.actionKey?.trim().toLowerCase() ?? null;

        if (isActionSummary && isActionOutput) {
          if (actionKey && activeActionCommandByKey.has(actionKey)) {
            resolvedCommandId = activeActionCommandByKey.get(actionKey) ?? resolvedCommandId;
          } else if (lastActiveActionCommandId) {
            resolvedCommandId = lastActiveActionCommandId;
          }
        } else if (isActionSummary && summary.actionKind === 'command' && actionKey) {
          const existingId = activeActionCommandByKey.get(actionKey);
          if (existingId) {
            const existing = commands.get(existingId);
            if (existing?.status === 'running') {
              resolvedCommandId = existingId;
            }
          }
        }

        const command = getOrCreateCommand(resolvedCommandId, summary.stageId, summary.step);
        command.stage = summary.stageId;
        command.step = summary.step;
        command.status = summary.status;
        if (summary.commandText && summary.commandText.trim()) {
          command.command = summary.commandText.trim();
        }
        command.latest_event_id = Math.max(command.latest_event_id, event.id);
        command.output_count += 1;
        command.output_tail.push({
          id: summary.eventId,
          stream: 'update',
          text: summary.text,
          created_at: summary.createdAt
        });

        if (!command.started_at) {
          command.started_at = summary.createdAt;
        }
        if (summary.status === 'running') {
          command.finished_at = null;
          if (actionKey) {
            activeActionCommandByKey.set(actionKey, command.id);
          }
          activeCommandId = command.id;
          lastActiveActionCommandId = command.id;
        } else {
          command.finished_at = summary.createdAt;
          if (actionKey) {
            activeActionCommandByKey.delete(actionKey);
          }
          if (activeCommandId === command.id) {
            activeCommandId = null;
          }
          if (lastActiveActionCommandId === command.id) {
            lastActiveActionCommandId = null;
          }
        }
      }
      continue;
    }
    if (event.stream !== 'stdout' && event.stream !== 'stderr') {
      continue;
    }
    const parsed = parseRunnerOutputLine(event.payload);
    const explicitCommandId =
      parsed.commandId === LEGACY_UNASSIGNED_COMMAND_ID ? FALLBACK_OUTPUT_COMMAND_ID : parsed.commandId;
    const commandId = explicitCommandId ?? activeCommandId ?? FALLBACK_OUTPUT_COMMAND_ID;
    let command = commands.get(commandId);
    if (!command && parsed.step) {
      command = Array.from(commands.values()).find((value) => value.step === parsed.step);
    }
    const defaultStep = RUNNER_COMMAND_STEP_LABELS[commandId] ?? commandId;
    const resolved = command ?? getOrCreateCommand(commandId, 'run', parsed.step ?? defaultStep);
    resolved.latest_event_id = Math.max(resolved.latest_event_id, event.id);
    resolved.output_count += 1;
    resolved.output_tail.push({
      id: event.id,
      stream: event.stream,
      text: parsed.text,
      created_at: event.created_at
    });
    if (resolved.output_tail.length > outputTailLimit) {
      resolved.output_tail = resolved.output_tail.slice(resolved.output_tail.length - outputTailLimit);
      resolved.has_more_output = true;
    }
  }

  const sortedCommands = Array.from(commands.values())
    .map((command) => ({
      ...command,
      has_more_output: command.has_more_output || command.output_count > command.output_tail.length
    }))
    .sort((a, b) => a.latest_event_id - b.latest_event_id);

  const stages = groupCommandsByHighLevelStage(sortedCommands, stageLabelForId);

  return {
    run_id: run.id,
    message_id: run.message_id,
    status: run.status,
    started_at: run.started_at,
    finished_at: run.finished_at,
    has_more_commands: false,
    next_before_event_id: null,
    stages
  };
};
